import React, { Component, Fragment } from 'react';
import axios from 'axios';
import moment from 'moment';
import { API_BASE_URL } from '../../config/api';

import {
  Modal,
  Row,
  Col,
  Button,
  Space,
  Table,
  Input,
  Select,
  Form,
  Divider,
  Tooltip,
  message,
} from 'antd';
import { SearchOutlined, EditOutlined } from '@ant-design/icons';

import { authService } from '../../services/authService';
import { states } from '../utilities';

const EMPTY_EDIT = {
  id: null,
  firstName: '',
  lastName: '',
  dob: '',
  gender: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  email: '',
};

// Patient search backed by the portal-owned connectPatients table. Rows can
// be edited in place (demographics only; insurance is updated when an order is
// submitted).
class PatientModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      clientCode: props.client.ClientCode,
      open: false,
      searchLastName: '',
      searchFirstName: '',
      searchDOB: '',
      patients: [],
      loading: false,
      limit: 10,
      currentPage: 1,
      totalCount: 0,

      editOpen: false,
      edit: { ...EMPTY_EDIT },
      saving: false,
    };
  }

  onClickModal = () => {
    this.setState(
      (prev) => ({ open: !prev.open }),
      () => {
        if (this.state.open) {
          this.clearFilters(() => this.loadPatients({ page: 1 }));
        }
      }
    );
  };

  loadPatients = async ({ page = this.state.currentPage, pageSize = this.state.limit } = {}) => {
    this.setState({ loading: true });

    const token = await authService.ensureValidToken();
    if (!token) {
      console.error('Not authenticated');
      this.setState({ loading: false });
      return;
    }

    const params = {
      skip: (page - 1) * pageSize,
      limit: pageSize,
      clientCode: this.state.clientCode,
      lastName: this.state.searchLastName || undefined,
      firstName: this.state.searchFirstName || undefined,
      dob: this.state.searchDOB || undefined,
    };

    try {
      const res = await axios.get(`${API_BASE_URL}/connect/patients`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      const items = res.data.items || [];
      const totalCount = res.data.totalCount ?? res.data.total ?? 0;

      this.setState({
        patients: items,
        totalCount,
        limit: pageSize,
        currentPage: page,
        loading: false,
      });
    } catch (err) {
      console.error(err);
      this.setState({ loading: false });
    }
  };

  handleSearch = () => {
    this.loadPatients({ page: 1 });
  };

  handlePageChange = (page, pageSize) => {
    this.loadPatients({ page, pageSize });
  };

  handleFilterChange = (field, value) => {
    this.setState({ [field]: value });
  };

  clearFilters = (callback) => {
    this.setState(
      {
        searchLastName: '',
        searchFirstName: '',
        searchDOB: '',
        currentPage: 1,
      },
      callback
    );
  };

  handleClear = () => {
    this.clearFilters(() => this.loadPatients({ page: 1 }));
  };

  handleSelectPatient = (record) => {
    this.props.onSelectPatient?.(record);
    this.setState({ open: false });
  };

  // ---------- edit ----------
  openEdit = (record) => {
    this.setState({
      editOpen: true,
      edit: {
        id: record.id,
        firstName: record.firstName || '',
        lastName: record.lastName || '',
        dob: record.dob ? moment.utc(record.dob).format('MM/DD/YYYY') : '',
        gender: record.gender || '',
        address: record.address || '',
        city: record.city || '',
        state: record.state || '',
        zip: record.zip || '',
        phone: record.phone || '',
        email: record.email || '',
      },
    });
  };

  closeEdit = () => this.setState({ editOpen: false, edit: { ...EMPTY_EDIT } });

  onEditChange = (field, value) => {
    this.setState((prev) => ({ edit: { ...prev.edit, [field]: value } }));
  };

  saveEdit = async () => {
    const { edit, clientCode } = this.state;
    if (!edit.firstName.trim() || !edit.lastName.trim()) {
      return message.warning('First and last name are required');
    }
    if (edit.dob && !moment(edit.dob, 'MM/DD/YYYY', true).isValid()) {
      return message.warning('DOB must be MM/DD/YYYY');
    }

    const token = await authService.ensureValidToken();
    if (!token) return message.error('Not authenticated');
    const user = authService.getUserAttributes() || {};

    this.setState({ saving: true });
    try {
      const res = await axios.put(
        `${API_BASE_URL}/connect/patients/${edit.id}`,
        {
          clientCode,
          firstName: edit.firstName.trim(),
          lastName: edit.lastName.trim(),
          dob: edit.dob || null,
          gender: edit.gender || null,
          address: edit.address,
          city: edit.city,
          state: edit.state,
          zip: edit.zip,
          phone: edit.phone,
          email: edit.email,
          updatedBy: user.preferred_username || user.email || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = res.data.item;
      this.setState((prev) => ({
        saving: false,
        editOpen: false,
        edit: { ...EMPTY_EDIT },
        patients: prev.patients.map((p) => (p.id === updated.id ? updated : p)),
      }));
      message.success('Patient updated');
    } catch (err) {
      console.error(err);
      this.setState({ saving: false });
      message.error(err.response?.data?.error || 'Could not update patient');
    }
  };

  renderEditModal() {
    const { editOpen, edit, saving } = this.state;
    const field = (label, key, extra = {}) => (
      <Form.Item label={label} style={{ marginBottom: 8 }} {...extra.item}>
        <Input
          value={edit[key]}
          onChange={(e) => this.onEditChange(key, e.target.value)}
          {...extra.input}
        />
      </Form.Item>
    );

    return (
      <Modal
        title="Edit Patient"
        centered
        open={editOpen}
        onCancel={this.closeEdit}
        onOk={this.saveEdit}
        okText="Save"
        confirmLoading={saving}
        width={640}
        destroyOnHidden
      >
        <Form layout="vertical" size="small">
          <Row gutter={8}>
            <Col span={12}>{field('Last Name', 'lastName', { item: { required: true } })}</Col>
            <Col span={12}>{field('First Name', 'firstName', { item: { required: true } })}</Col>
            <Col span={12}>{field('DOB', 'dob', { input: { placeholder: 'MM/DD/YYYY' } })}</Col>
            <Col span={12}>
              <Form.Item label="Gender" style={{ marginBottom: 8 }}>
                <Select
                  value={edit.gender || undefined}
                  allowClear
                  placeholder="Select"
                  onChange={(v) => this.onEditChange('gender', v || '')}
                  options={[
                    { value: 'F', label: 'Female' },
                    { value: 'M', label: 'Male' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={24}>{field('Address', 'address')}</Col>
            <Col span={10}>{field('City', 'city')}</Col>
            <Col span={7}>
              <Form.Item label="State" style={{ marginBottom: 8 }}>
                <Select
                  showSearch
                  allowClear
                  value={edit.state || undefined}
                  placeholder="State"
                  onChange={(v) => this.onEditChange('state', v || '')}
                  options={states.map((s) => ({ value: s.abbreviation, label: s.abbreviation }))}
                />
              </Form.Item>
            </Col>
            <Col span={7}>{field('Zip', 'zip')}</Col>
            <Col span={12}>{field('Phone', 'phone')}</Col>
            <Col span={12}>{field('Email', 'email')}</Col>
          </Row>
        </Form>
      </Modal>
    );
  }

  render() {
    const columns = [
      { title: 'Last Name', dataIndex: 'lastName', key: 'lastName' },
      { title: 'First Name', dataIndex: 'firstName', key: 'firstName' },
      {
        title: 'DOB',
        dataIndex: 'dob',
        key: 'dob',
        render: (value) => (value ? moment.utc(value).format('MM/DD/YYYY') : ''),
      },
      { title: 'Address', dataIndex: 'address', key: 'address', responsive: ['md'] },
      { title: 'City', dataIndex: 'city', key: 'city', responsive: ['md'] },
      { title: 'State', dataIndex: 'state', key: 'state', responsive: ['md'] },
      { title: 'Zip', dataIndex: 'zip', key: 'zip', responsive: ['md'] },
      {
        title: '',
        key: 'edit',
        width: 50,
        align: 'center',
        render: (_, record) => (
          <Tooltip title="Edit patient">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                this.openEdit(record);
              }}
            />
          </Tooltip>
        ),
      },
    ];

    return (
      <Fragment>
        <Button type="primary" ghost size="small" block onClick={this.onClickModal}>
          <SearchOutlined /> Search Patients
        </Button>

        <Modal
          title="Search Patients"
          centered
          open={this.state.open}
          onOk={this.onClickModal}
          onCancel={this.onClickModal}
          width="80%"
          footer={null}
        >
          <Row>
            <Col span={24}>
              <Space wrap style={{ width: '100%' }}>
                <Input
                  placeholder="Last Name"
                  allowClear
                  value={this.state.searchLastName}
                  onChange={(e) => this.handleFilterChange('searchLastName', e.target.value)}
                  onPressEnter={this.handleSearch}
                  style={{ width: 150 }}
                />
                <Input
                  placeholder="First Name"
                  allowClear
                  value={this.state.searchFirstName}
                  onChange={(e) => this.handleFilterChange('searchFirstName', e.target.value)}
                  onPressEnter={this.handleSearch}
                  style={{ width: 150 }}
                />
                <Input
                  placeholder="DOB (MM/DD/YYYY)"
                  allowClear
                  value={this.state.searchDOB}
                  onChange={(e) => this.handleFilterChange('searchDOB', e.target.value)}
                  onPressEnter={this.handleSearch}
                  style={{ width: 170 }}
                />
                <Button type="primary" onClick={this.handleSearch}>
                  <SearchOutlined /> Search
                </Button>
                <Button onClick={this.handleClear}>Clear</Button>
              </Space>
            </Col>
            <Divider />
            <Col span={24}>
              <Table
                columns={columns}
                dataSource={this.state.patients}
                loading={this.state.loading}
                size="small"
                rowKey={(row) => row.id}
                onRow={(record) => ({
                  onClick: () => this.handleSelectPatient(record),
                  style: { cursor: 'pointer' },
                })}
                pagination={{
                  current: this.state.currentPage,
                  pageSize: this.state.limit,
                  total: this.state.totalCount,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '25', '50', '100'],
                  onChange: this.handlePageChange,
                  onShowSizeChange: this.handlePageChange,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                }}
              />
            </Col>
          </Row>
        </Modal>

        {this.renderEditModal()}
      </Fragment>
    );
  }
}

export default PatientModal;
